/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import type { UploadFile } from 'ember-file-upload';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  DISPUTE_EVIDENCE_FILE_FIELDS,
  DISPUTE_EVIDENCE_TEXT_FIELDS,
  type DisputeEvidenceFileField,
  type DisputeEvidenceTextField,
} from 'uplisting-frontend/models/dispute';
import {
  DISPUTE_EVIDENCE_FILE_TYPES,
  DISPUTE_EVIDENCE_MAX_FILE_BYTES,
  type DisputeEvidenceDraft,
} from 'uplisting-frontend/services/repositories/dispute';

export const MAX_EVIDENCE_LENGTH = 150_000;

const CORE_FIELDS: DisputeEvidenceTextField[] = [
  'customer_name',
  'customer_email_address',
  'product_description',
  'uncategorized_text',
];

const LONG_FIELDS: DisputeEvidenceTextField[] = [
  'access_activity_log',
  'billing_address',
  'cancellation_policy_disclosure',
  'cancellation_rebuttal',
  'duplicate_charge_explanation',
  'product_description',
  'refund_policy_disclosure',
  'refund_refusal_explanation',
  'shipping_address',
  'uncategorized_text',
];

export interface IEvidenceInput {
  key: DisputeEvidenceTextField;
  value: string;
  isLong: boolean;
}

export interface IEvidenceFileSlot {
  key: DisputeEvidenceFileField;
  isAttached: boolean;
}

interface IArgs {
  dispute: ModelRegistry['dispute'];
}

interface UiProtectionDisputePanelResponseSignature {
  Element: HTMLElement;

  Args: IArgs;
}

export default class UiProtectionDisputePanelResponseComponent extends Component<UiProtectionDisputePanelResponseSignature> {
  @service session!: Services['session'];
  @service notifications!: Services['notifications'];
  @service sentry!: Services['sentry'];

  @service('repositories/dispute')
  disputeRepository!: Services['repositories/dispute'];

  @tracked draft: DisputeEvidenceDraft = {};
  @tracked isSaving = false;
  @tracked isPrefilling = false;
  @tracked isSubmitting = false;
  @tracked isAccepting = false;
  @tracked isRefunding = false;
  @tracked hasRefunded = false;
  @tracked showAllFields = false;
  @tracked showSubmitConfirm = false;
  @tracked showAcceptConfirm = false;
  @tracked showRefundConfirm = false;
  @tracked attachingField?: DisputeEvidenceFileField;
  @tracked downloadingField?: DisputeEvidenceFileField;

  @cached
  get canEdit(): boolean {
    return (
      this.args.dispute.isRespondable &&
      !!this.session.currentUser?.hasAccessTo('disputes.respond')
    );
  }

  @cached
  get canPrefill(): boolean {
    return this.canEdit && !!this.args.dispute.booking;
  }

  @cached
  get coreInputs(): IEvidenceInput[] {
    return CORE_FIELDS.map((key) => this.#input(key));
  }

  @cached
  get extraInputs(): IEvidenceInput[] {
    return DISPUTE_EVIDENCE_TEXT_FIELDS.filter(
      (key) => !CORE_FIELDS.includes(key),
    ).map((key) => this.#input(key));
  }

  @cached
  get fileSlots(): IEvidenceFileSlot[] {
    const files = this.args.dispute.stagedFiles;

    return DISPUTE_EVIDENCE_FILE_FIELDS.map((key) => ({
      key,
      isAttached: !!files[key],
    }));
  }

  @cached
  get changedFields(): DisputeEvidenceDraft {
    const staged = this.args.dispute.stagedFields;

    return Object.fromEntries(
      Object.entries(this.draft).filter(
        ([key, value]) => (staged[key] ?? '') !== value,
      ),
    );
  }

  @cached
  get hasChanges(): boolean {
    return Object.keys(this.changedFields).length !== 0;
  }

  @cached
  get charactersUsed(): number {
    return this.coreInputs
      .concat(this.extraInputs)
      .reduce((total, input) => total + input.value.length, 0);
  }

  @cached
  get isOverLimit(): boolean {
    return this.charactersUsed > MAX_EVIDENCE_LENGTH;
  }

  @cached
  get characterLimit(): number {
    return MAX_EVIDENCE_LENGTH;
  }

  @cached
  get isBusy(): boolean {
    return (
      this.isSaving ||
      this.isPrefilling ||
      this.isSubmitting ||
      this.isAccepting ||
      this.isRefunding ||
      !!this.attachingField
    );
  }

  @cached
  get isSaveDisabled(): boolean {
    return this.isBusy || this.isOverLimit || !this.hasChanges;
  }

  @cached
  get isSubmitDisabled(): boolean {
    return (
      this.isBusy ||
      this.isOverLimit ||
      !(this.hasChanges || this.args.dispute.hasStagedEvidence)
    );
  }

  @action
  handleChange(key: DisputeEvidenceTextField, event: Event): void {
    const { value } = event.target as HTMLInputElement;

    this.draft = { ...this.draft, [key]: value };
  }

  @action
  handleShowAllFields(): void {
    this.showAllFields = true;
  }

  @action
  handleConfirmSubmit(): void {
    this.showSubmitConfirm = true;
  }

  @action
  handleCancelSubmit(): void {
    this.showSubmitConfirm = false;
  }

  @action
  handleConfirmAccept(): void {
    this.showAcceptConfirm = true;
  }

  @action
  handleCancelAccept(): void {
    this.showAcceptConfirm = false;
  }

  @action
  handleConfirmRefund(): void {
    this.showRefundConfirm = true;
  }

  @action
  handleCancelRefund(): void {
    this.showRefundConfirm = false;
  }

  @action
  @waitFor
  async handlePrefill(): Promise<void> {
    this.isPrefilling = true;

    try {
      const suggestions = await this.disputeRepository.prefill(
        this.args.dispute,
      );

      this.draft = { ...this.draft, ...suggestions };
      this.showAllFields = true;
      this.notifications.info('protection_disputes.panel.response.prefilled');
    } catch (error) {
      this.#reportFailure(error);
    } finally {
      this.isPrefilling = false;
    }
  }

  @action
  @waitFor
  async handleSave(): Promise<void> {
    this.isSaving = true;

    try {
      await this.disputeRepository.saveEvidence(
        this.args.dispute,
        this.changedFields,
      );

      this.draft = {};
      this.notifications.info('protection_disputes.panel.response.saved');
    } catch (error) {
      this.#reportFailure(error);
    } finally {
      this.isSaving = false;
    }
  }

  @action
  @waitFor
  async handleSubmit(): Promise<void> {
    this.showSubmitConfirm = false;
    this.isSubmitting = true;

    try {
      await this.disputeRepository.submitEvidence(
        this.args.dispute,
        this.changedFields,
      );

      this.draft = {};
      this.notifications.info('protection_disputes.panel.response.submitted');
    } catch (error) {
      this.#reportFailure(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  @action
  @waitFor
  async handleAccept(): Promise<void> {
    this.showAcceptConfirm = false;
    this.isAccepting = true;

    try {
      await this.disputeRepository.accept(this.args.dispute);

      this.notifications.info('protection_disputes.panel.response.accepted');
    } catch (error) {
      this.#reportFailure(error);
    } finally {
      this.isAccepting = false;
    }
  }

  @action
  @waitFor
  async handleRefund(): Promise<void> {
    this.showRefundConfirm = false;
    this.isRefunding = true;

    try {
      await this.disputeRepository.refund(this.args.dispute);

      this.hasRefunded = true;
      this.notifications.info('protection_disputes.panel.response.refunded');
    } catch (error) {
      this.#reportFailure(error);
    } finally {
      this.isRefunding = false;
    }
  }

  @action
  @waitFor
  async handleAttach(
    field: DisputeEvidenceFileField,
    file: UploadFile,
  ): Promise<void> {
    if (!this.#isAcceptable(file.file)) {
      this.notifications.error(
        'protection_disputes.panel.response.file_invalid',
      );

      return;
    }

    this.attachingField = field;

    try {
      await this.disputeRepository.attachFile(
        this.args.dispute,
        field,
        file.file,
      );
    } catch (error) {
      this.#reportFailure(error);
    } finally {
      this.attachingField = undefined;
    }
  }

  @action
  @waitFor
  async handleRemove(field: DisputeEvidenceFileField): Promise<void> {
    this.attachingField = field;

    try {
      await this.disputeRepository.detachFile(this.args.dispute, field);
    } catch (error) {
      this.#reportFailure(error);
    } finally {
      this.attachingField = undefined;
    }
  }

  @action
  @waitFor
  async handleDownload(field: DisputeEvidenceFileField): Promise<void> {
    this.downloadingField = field;

    try {
      await this.disputeRepository.downloadFile(this.args.dispute, field);
    } catch (error) {
      this.#reportFailure(error);
    } finally {
      this.downloadingField = undefined;
    }
  }

  #input(key: DisputeEvidenceTextField): IEvidenceInput {
    const staged = this.args.dispute.stagedFields[key] ?? '';

    return {
      key,
      value: this.draft[key] ?? staged,
      isLong: LONG_FIELDS.includes(key),
    };
  }

  #isAcceptable(file: File): boolean {
    return (
      DISPUTE_EVIDENCE_FILE_TYPES.includes(file.type) &&
      file.size <= DISPUTE_EVIDENCE_MAX_FILE_BYTES
    );
  }

  #reportFailure(error: unknown): void {
    const detail = (
      error as { response?: { data?: { errors?: { detail?: string }[] } } }
    )?.response?.data?.errors?.[0]?.detail;

    if (detail) {
      this.notifications.error(detail, true);

      return;
    }

    this.sentry.captureException(error);
    this.notifications.error();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Protection::DisputePanel::Response': typeof UiProtectionDisputePanelResponseComponent;
    'ui/protection/dispute-panel/response': typeof UiProtectionDisputePanelResponseComponent;
  }
}
